import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/setup';
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import type { SelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import { PermissionManager } from '@/__core/permissions/utils/PermissionManager';
import { mergeRolePermissions } from '@/__core/permissions/utils/permissionResolver';

/**
 * Menu/sidebar permissions — always from login-selected company (storage),
 * not from list-toolbar company filters or auth.companies scope.
 */
export const useMenuPermissions = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);
  const hasGlobalRole = useSelector((state: RootState) => state.auth.hasGlobalRole);
  const reduxSelectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);

  const loginSelectedCompany: SelectedCompany | null =
    getSelectedCompany() ?? reduxSelectedCompany ?? null;

  const permissionManager = useMemo(() => {
    if (!user || !loginSelectedCompany) {
      return null;
    }

    return new PermissionManager({
      user: {
        id: String(user.id),
        email: user.email || '',
        username: user.username || '',
      },
      globalRoles: globalRoles || [],
      hasGlobalRole,
      selectedCompany: {
        company_id: loginSelectedCompany.company_id || '',
        company_name: loginSelectedCompany.company_name || '',
        company_code: loginSelectedCompany.company_code || '',
        company_type: loginSelectedCompany.company_type || 'parent',
        roles: loginSelectedCompany.roles || [],
      },
    });
  }, [
    user,
    loginSelectedCompany?.company_id,
    loginSelectedCompany?.selected_at,
    loginSelectedCompany?.roles,
    globalRoles,
    hasGlobalRole,
  ]);

  const hasPermission = (resource: string, action: string): boolean => {
    if (!permissionManager) return false;
    return permissionManager.hasPermission(resource, action);
  };

  const hasRole = (roleName: string): boolean => {
    if (!permissionManager) return false;
    return permissionManager.hasRole(roleName);
  };

  const hasAnyRole = (roleNames: string[]): boolean => {
    if (!permissionManager) return false;
    return roleNames.some((roleName) => permissionManager.hasRole(roleName));
  };

  const getAllPermissions = (): Record<string, string[]> => {
    if (!loginSelectedCompany?.roles) return {};
    return mergeRolePermissions(loginSelectedCompany.roles);
  };

  return {
    hasPermission,
    hasRole,
    hasAnyRole,
    getAllPermissions,
    loginSelectedCompany,
    permissionManager,
  };
};

export default useMenuPermissions;
